import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, inject, Input } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSliderModule } from '@angular/material/slider';
import { noop } from 'lodash';

type OnChangeFunction = (value: number) => void;
type OnTouchFunction = () => void;

@Component({
  selector: 'la-angle',
  templateUrl: './angle.component.html',
  styleUrl: './angle.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, MatSliderModule, MatFormFieldModule, MatInputModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AngleComponent),
      multi: true,
    },
  ],
})
export class AngleComponent implements ControlValueAccessor {
  private cd = inject(ChangeDetectorRef);

  @Input() min = 0;
  @Input() max = 100;
  @Input() step = 1;
  @Input() label = '';

  public sliderValue = 0;

  onChange: OnChangeFunction = noop;
  onTouch: OnTouchFunction = noop;

  registerOnChange(fn: OnChangeFunction): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: OnTouchFunction): void {
    this.onTouch = fn;
  }

  writeValue(input: number) {
    this.sliderValue = +input;
    this.onChange(+this.sliderValue);
  }

  inputChange() {
    this.passValue(+this.sliderValue);
  }

  passValue(value: number) {
    value = this.sanitizeValue(value);
    this.sliderValue = value;
    this.onChange(this.sliderValue);
    this.cd.markForCheck();
  }

  private sanitizeValue(value: number): number {
    if (value < this.min) return this.min;
    if (value > this.max) return this.max;
    return value;
  }
}
